// The band: a day from 05:00 to 22:00, the ride drawn on it as an outline, the fill
// the light in winter and the weather in summer. Pure functions; the component is Band.tsx.
// Every position is a share of the axis so the same minute always sits in the same place.

export const AXIS_START = 5 * 60;   // 05:00
export const AXIS_END = 22 * 60;    // 22:00
export const AXIS_MIN = AXIS_END - AXIS_START; // 1020 minutes

export type Span = { start: number; end: number };                 // minutes after midnight
export type Ride = Span & { done?: boolean; label?: string };
export type Kept = Span & { label: string };
export type Hourly = { feels: number[]; rain: boolean[]; wind: number[] }; // 17 entries, 05:00 to 21:00

export type Day = {
  date: string;            // ISO date
  rise: number;            // sunrise, minutes after local midnight (computed, never looked up)
  set: number;             // sunset
  rides?: Ride[];
  kept?: Kept[];
  rest?: boolean;
  hourly?: Hourly | null;  // null when the forecast is out of range
};

export type Fill = "light" | "weather";

/** Share of the axis, 0 to 1, clamped. */
export const pos = (min: number) => Math.min(1, Math.max(0, (min - AXIS_START) / AXIS_MIN));
export const pct = (min: number) => `${(pos(min) * 100).toFixed(2)}%`;

/** Minutes of a span that fall outside sunrise to sunset. */
export function darkMinutes(span: Span, rise: number, set: number): number {
  const lit = Math.max(0, Math.min(span.end, set) - Math.max(span.start, rise));
  return Math.max(0, span.end - span.start - lit);
}

/** The day decides: light if any planned ride has a dark minute or the day is under 12 hours, else weather. */
export function fillFor(day: Day): Fill {
  const dayLength = day.set - day.rise;
  if (dayLength < 12 * 60) return "light";
  for (const r of day.rides ?? []) if (darkMinutes(r, day.rise, day.set) > 0) return "light";
  return "weather";
}

export type Stop = { at: number; color: string }; // at = share of the axis, 0 to 1

/** The light: night, an amber bloom at sunrise, dawn, day, dusk at sunset, night. Stops sit on the computed sun. */
export function lightStops(rise: number, set: number): Stop[] {
  const r = pos(rise), s = pos(set);
  const w = 0.03; // the bloom's width either side, as a share of the axis
  return [
    { at: 0, color: "var(--light-night)" },
    { at: Math.max(0, r - w), color: "var(--light-night)" },
    { at: r, color: "var(--sunrise)" },
    { at: Math.min(1, r + w * 1.6), color: "var(--light-dawn)" },
    { at: Math.min(1, r + w * 4), color: "var(--light-day)" },
    { at: Math.max(0, s - w * 2), color: "var(--light-dawn)" },
    { at: s, color: "var(--dusk)" },
    { at: Math.min(1, s + w * 2.2), color: "var(--light-night)" },
    { at: 1, color: "var(--light-night)" },
  ];
}

/** Feels-like to a colour: cold at 8 and below, neutral at 15, amber at 22, hot at 28 and above. */
export function feelsColor(feels: number): string {
  if (feels <= 8) return "var(--weather-cold)";
  if (feels < 15) return mix("var(--weather-cold)", "var(--light-day)", (feels - 8) / 7);
  if (feels < 22) return mix("var(--light-day)", "var(--sunrise)", (feels - 15) / 7);
  if (feels < 28) return mix("var(--sunrise)", "var(--weather-hot)", (feels - 22) / 6);
  return "var(--weather-hot)";
}
const mix = (a: string, b: string, t: number) => `color-mix(in oklab, ${a} ${Math.round((1 - t) * 100)}%, ${b})`;

/** The weather: one stop per hour from 05:00 to 21:00, then the last hour held to 22:00. */
export function weatherStops(h: Hourly): Stop[] {
  const stops = h.feels.map((f, i) => ({ at: pos(AXIS_START + i * 60), color: feelsColor(f) }));
  stops.push({ at: 1, color: feelsColor(h.feels[h.feels.length - 1]) });
  return stops;
}

export const gradient = (stops: Stop[]) =>
  `linear-gradient(90deg, ${stops.map((s) => `${s.color} ${(s.at * 100).toFixed(2)}%`).join(", ")})`;

/** Hours with rain forecast, as spans on the axis for the hatch. */
export function rainSpans(h: Hourly): Span[] {
  const out: Span[] = [];
  h.rain.forEach((wet, i) => {
    const start = AXIS_START + i * 60;
    if (!wet) return;
    const last = out[out.length - 1];
    if (last && last.end === start) last.end = start + 60; else out.push({ start, end: start + 60 });
  });
  return out;
}

/** The high of the day and its hour, for the weather fill's tick. */
export function high(h: Hourly): { feels: number; at: number } {
  let i = 0;
  h.feels.forEach((f, k) => { if (f > h.feels[i]) i = k; });
  return { feels: h.feels[i], at: AXIS_START + i * 60 };
}

export const hhmm = (min: number) => {
  const r = Math.round(min);
  return `${String(Math.floor(r / 60)).padStart(2, "0")}:${String(r % 60).padStart(2, "0")}`;
};

/**
 * The folded axis, for dragging a ride. Blocked time (work, kept evenings, after the latest
 * finish) folds down to a sliver each, and the free windows share the rest of the width, so
 * nearly all of the band answers the finger. P maps a minute to a share of the width; M is
 * its inverse. Unfolded, both are the plain linear axis.
 */
export type Axis = { P: (min: number) => number; M: (share: number) => number; folds: { start: number; end: number; at: number; w: number }[] };
export function foldAxis(walls: Span[], fold = true, sliver = 0.05): Axis {
  const lin: Axis = { P: pos, M: (s) => AXIS_START + Math.min(1, Math.max(0, s)) * AXIS_MIN, folds: [] };
  if (!fold || !walls.length) return lin;
  // Clip and merge the walls.
  const ws = walls.map((w) => ({ start: Math.max(AXIS_START, w.start), end: Math.min(AXIS_END, w.end) })).filter((w) => w.end > w.start).sort((a, b) => a.start - b.start);
  const merged: Span[] = [];
  for (const w of ws) { const l = merged[merged.length - 1]; if (l && w.start <= l.end) l.end = Math.max(l.end, w.end); else merged.push({ ...w }); }
  // Segments across the axis: wall or free, each with its minutes.
  const segs: { start: number; end: number; wall: boolean }[] = [];
  let t = AXIS_START;
  for (const w of merged) { if (w.start > t) segs.push({ start: t, end: w.start, wall: false }); segs.push({ start: w.start, end: w.end, wall: true }); t = w.end; }
  if (t < AXIS_END) segs.push({ start: t, end: AXIS_END, wall: false });
  const freeMin = segs.filter((s) => !s.wall).reduce((a, s) => a + s.end - s.start, 0);
  if (!freeMin) return lin;
  // A wall keeps its own share if that is already slimmer than the sliver.
  const wallW = segs.filter((s) => s.wall).map((s) => Math.min(sliver, (s.end - s.start) / AXIS_MIN));
  const left = 1 - wallW.reduce((a, b) => a + b, 0);
  let at = 0, k = 0;
  const placed = segs.map((s) => { const w = s.wall ? wallW[k++] : ((s.end - s.start) / freeMin) * left; const o = { ...s, at, w }; at += w; return o; });
  const P = (min: number) => {
    const m = Math.min(AXIS_END, Math.max(AXIS_START, min));
    const s = placed.find((x) => m >= x.start && m <= x.end) ?? placed[placed.length - 1];
    return s.at + ((m - s.start) / (s.end - s.start)) * s.w;
  };
  const M = (share: number) => {
    const p = Math.min(1, Math.max(0, share));
    const s = placed.find((x) => p >= x.at && p <= x.at + x.w) ?? placed[placed.length - 1];
    return s.start + (s.w ? (p - s.at) / s.w : 0) * (s.end - s.start);
  };
  return { P, M, folds: placed.filter((s) => s.wall).map((s) => ({ start: s.start, end: s.end, at: s.at, w: s.w })) };
}

/** Colour stops placed on a folded axis: each stop's minute moved to where that minute now sits. */
export const foldStops = (stops: Stop[], ax: Axis): Stop[] => stops.map((s) => ({ ...s, at: ax.P(AXIS_START + s.at * AXIS_MIN) }));
